/**
 * Eigenbau: Rubrikenzeile über einer Tintenlinie — Tabellenkopf ohne Tabelle.
 *
 * Führt eine Liste an: links die Bezeichnung mono in Gewicht 500, rechts
 * daneben ruhig die Anzahl („6 von 41"), darunter eine volle Tintenlinie.
 *
 * Nicht Tremors `TableHead`: Der Entwurf hat an diesen Stellen keine Tabelle,
 * sondern eine Reihe von Zeilen ohne Spaltenraster. Ein `<thead>` würde eine
 * Struktur behaupten, die es nicht gibt, und Vorleseprogrammen eine Tabelle
 * ankündigen, in der sich niemand spaltenweise bewegen kann.
 *
 * `items-baseline`: Bezeichnung und Anzahl haben verschiedene Grade (12.5 und
 * 11.5) und müssen trotzdem auf einer Linie sitzen.
 */

import type { ReactNode } from "react";
import { cx } from "@/lib/utils";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";

export function Rubrik({
  children,
  anzahl,
  aktion,
  id,
  className,
}: {
  children: ReactNode;
  /** Ruhige Zählung neben der Bezeichnung — „6 von 41". */
  anzahl?: ReactNode;
  /** Rechts außen, etwa ein Filter oder ein Verweis. */
  aktion?: ReactNode;
  /**
   * Macht die Rubrik zur echten Überschrift `<h2>` mit dieser Kennung.
   *
   * Ohne `id` bleibt sie ein `<span>` — sie sieht ja aus wie eine
   * Überschrift, ist aber nicht überall eine. Wo sie eine Liste anführt,
   * gehört sie ins Überschriftengefüge und die Liste zeigt per
   * `aria-labelledby` auf sie. Vorher trug die Einstiegsseite genau eine
   * Überschrift, und fünf Listen kündigten sich einem Vorleseprogramm als
   * „Liste, 6 Einträge" ohne jeden Bezug an.
   */
  id?: string;
  className?: string;
}) {
  return (
    <div
      className={cx(
        "flex items-baseline gap-3.5 border-b border-tinte pb-3",
        className,
      )}
    >
      {id ? (
        <h2 id={id} className="contents">
          <Mikrolabel grad={12.5} ton="laut" betont>
            {children}
          </Mikrolabel>
        </h2>
      ) : (
        <Mikrolabel grad={12.5} ton="laut" betont>
          {children}
        </Mikrolabel>
      )}
      {anzahl ? <Mikrolabel zahl>{anzahl}</Mikrolabel> : null}
      {aktion ? <div className="ml-auto">{aktion}</div> : null}
    </div>
  );
}
